/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  CompanyResponse,
  ContractTypeResponse,
  DealerResponse,
  ContractRequest,
  useCompanies,
  useCompany,
  useDealers,
  useDealer,
  useContractType,
  useContractTypes,
  useTemplatesByCompany,
  useTemplatesByContractType,
  useProducts,
  useContract,
  useUpdateContract,
  useCreateContract,
  useReserveContractCode,
  useSendForSigning,
  useCreditCheck,
  useGetImage,
} from '@api'
import {
  addTopRightPageNumbers,
  contractSteps,
  generateDropdownOptions,
  generatePdf,
  generateReadableErrorMessages,
  getTotalContractCost,
  getUserData,
  isSuperAdmin,
  useViewNavigate,
  validateFields,
} from '@common'
import {
  ContractStatus,
  ContractType,
  ContractVariant,
  Country,
  ImageType,
  NavigationPathKey,
  TranslationKey,
} from '@enums'
import {
  errorAtom,
  isFormDirtyAtom,
  handleNavigateAtom,
  pageLinksAtom,
  contractFormDataAtom,
  productDataAtom,
  customerDataAtom,
  contractSetupDataAtom,
  extraCostsAtom,
  contractResourcesAtom,
  contractSetupFormFieldErrorsAtom,
  extraCostsFormFieldErrorsAtom,
  productFormFieldErrorsAtom,
  customerFormFieldErrorsAtom,
  contractDropdownOptionsAtom,
  signingFormDataAtom,
  getCompanyCurrencyAtom,
  getUserSelectionAtom,
  getUserTimeFormatAtom,
} from '@state'
import {initialCustomerData, initialProductData} from '@state'

import {SetStateAction, useEffect, useMemo, useState} from 'react'

import {addDays, formatDate, parse} from 'date-fns'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useLocation, useParams} from 'react-router-dom'

import {generateTemplateHtml} from './contract-form-steps/contract-pdf-overview/contract-overview-custom-html'
import {generateHtml} from './contract-form-steps/contract-pdf-overview/contract-overview-html'
import {ContractHTMLData} from './contract-form-steps/contract-pdf-overview/contract-pdf-overview-model'

export function useContactFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)

  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const {id} = useParams()

  //CONTRACT ATOMS
  const formData = useAtomValue(contractFormDataAtom)
  const setProductFormData = useSetAtom(productDataAtom)
  const setCustomerFormData = useSetAtom(customerDataAtom)
  const setContractSetupFormData = useSetAtom(contractSetupDataAtom)
  const setExtraCostsFormData = useSetAtom(extraCostsAtom)
  const setContractSetupFormFieldErrors = useSetAtom(contractSetupFormFieldErrorsAtom)
  const setExtraCostsFormFieldErrors = useSetAtom(extraCostsFormFieldErrorsAtom)
  const setProductFormFieldErrors = useSetAtom(productFormFieldErrorsAtom)
  const setCustomerFormFieldErrors = useSetAtom(customerFormFieldErrorsAtom)
  const setDropdownOptions = useSetAtom(contractDropdownOptionsAtom)
  const signingFormData = useAtomValue(signingFormDataAtom)
  //END CONTRACT ATOMS

  const [creditCheckFailed, setCreditCheckFailed] = useState(false)
  const [isSignatureOptionsModalVisible, setIsSignatureOptionsModalVisible] = useState<boolean>(false)
  const [isAgreementModalVisible, setIsAgreementModalVisible] = useState(false)

  const {data: contractResponse} = useContract(id)
  const {data: companiesData} = useCompanies()
  const {data: contractTypesData} = useContractTypes()
  const {data: dealersData} = useDealers()
  const {data: contractTemplatesData} = useTemplatesByCompany(formData?.contractSetupData?.company?.id)
  const {data: productsData} = useProducts()
  const {data: contractTypeTemplatesData} = useTemplatesByContractType(formData.contractSetupData.contractType?.id)
  const {data: imageUrl} = useGetImage(ImageType.COMPANY, formData?.contractSetupData?.company?.id)

  const {data: company} = useCompany(contractResponse?.companyId)
  const {data: dealer} = useDealer(contractResponse?.dealerId)
  const {data: contractType} = useContractType(contractResponse?.contractTypeId)

  const {mutate: updateContractMutation} = useUpdateContract()
  const {mutate: createContractMutation} = useCreateContract()
  const {refetch: reserveCodeQuery} = useReserveContractCode(formData.contractSetupData.company?.id || '', false)
  const {mutate: creditCheckMutation} = useCreditCheck()
  const {mutate: sendForSigningMutation} = useSendForSigning()

  const transferTemplate = useMemo(() => {
    if (!contractTemplatesData) return ''
    else return contractTemplatesData.find(item => item.contractType === 'TA')?.htmlContent
  }, [contractTemplatesData])

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getUserTimeFormat = useAtomValue(getUserTimeFormatAtom)
  const userTimeFormat = getUserTimeFormat(currentUser.id)

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const [activeStep, setActiveStep] = useState<number>(0)
  const setContractResources = useSetAtom(contractResourcesAtom)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isQuoteEdit = queryParams.get('editQuote')

  const steps = contractSteps(t)

  const isB2B = useMemo(
    () => formData.contractSetupData.contractType?.type === ContractType.B2B,
    [formData.contractSetupData],
  )

  useEffect(() => {
    if (id && contractResponse) {
      setContractSetupFormData({
        company,
        contractCode: contractResponse.contractCode,
        contractStatus: contractResponse.status,
        contractType,
        dealer,
        creditCheckPassed: contractResponse.creditCheckPassed,
        length: contractResponse.length,
        type: isQuoteEdit ? ContractVariant.QUOTE : ContractVariant.CREATION,
        expiry: contractResponse.expiry,
        createdAt: formatDate(contractResponse.createdAt, userTimeFormat),
        officialSignatory: contractResponse.officialSignatory,
      })
      setCustomerFormData(contractResponse.customer)
      setExtraCostsFormData(contractResponse.extraCosts)
      setProductFormData(
        contractResponse.products.map(product => ({
          ...product,
          id: product.productId,
          accessories: product.accessories.map(accessory => ({...accessory, id: accessory.accessoryId})),
        })),
      )

      setActiveStep(1)
    } else {
      setContractSetupFormData(prev => ({
        ...prev,
        company: companiesData?.find(item => item.id === userSelection?.companyId),
        ...(userSelection?.dealerId !== 'all'
          ? {dealer: dealersData?.find(item => item.id === userSelection?.dealerId)}
          : {}),
      }))
      setCustomerFormData(initialCustomerData)
      setExtraCostsFormData([])
      setProductFormData(initialProductData)
    }

    setContractResources({
      companies: companiesData || [],
      dealers: dealersData || [],
      contractTypes: contractTypesData || [],
      products: productsData || [],
      accessories: (productsData || [])?.flatMap(item => item.accessories),
    })

    setDropdownOptions(prev => ({
      companyOptions: generateDropdownOptions(companiesData || []),
      dealerOptions: generateDropdownOptions(dealersData || []),
      contractTypeOptions: userSelection?.dealerId
        ? generateDropdownOptions(
            (contractTypesData || [])
              ?.filter(item => item.allowedDealers.some(dealer => dealer.id === userSelection.dealerId))
              .map(item => ({...item, name: `${item.name} ${item.type}`})),
          )
        : prev.contractTypeOptions,
      productOptions: generateDropdownOptions(
        (productsData || [])?.map(item => ({...item, name: `${item.productNo} ${item.name}`})),
      ),
      accessoryOptions: (productsData || [])?.map(item => ({
        productId: item.id,
        options: generateDropdownOptions(
          item.accessories.map(item => ({...item, name: `${item.productNo} ${item.name}`})),
        ),
      })),
    }))

    if (isSuperAdmin()) {
      if (!id)
        setContractSetupFormData(prev => ({
          ...prev,
          company: companiesData?.find(item => item.id === userSelection?.companyId),
        }))

      setDropdownOptions(prev => ({
        ...prev,
        dealerOptions: generateDropdownOptions(
          (dealersData || [])?.filter(item => item.companyId === userSelection?.companyId),
        ),
      }))
    }

    if (userSelection?.dealerId && userSelection.dealerId !== 'all' && !id) {
      setContractSetupFormData(prev => ({
        ...prev,
        dealer: dealersData?.find(item => item.id === userSelection.dealerId),
      }))
    }
  }, [userSelection, contractResponse, id, company, dealer, dealersData, companiesData, productsData, contractType])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Contract search']), link: NavigationPathKey.CONTRACT_SEARCH},
        {title: t(TranslationKey['Contract creation'])},
      ]),
    [],
  )

  /**
   * A function handling Discard button click
   */
  const onLeftBtnClick = () => handleNavigate((): void => navigate(NavigationPathKey.CONTRACT_SEARCH))
  /**
   * A function handling Back button click
   */
  const onCenterBtnClick = () => setActiveStep(prev => prev - 1)

  /**
   * A custom validation function the the Contract setup form fields
   * @returns If the Contract setup form fields have invalid values
   */
  const validateSetupFormFields = (): string[] => {
    const notRequredSetupFields = ['contractType', 'expiry', 'length', 'createdAt', 'officialSignatory', 'contractCode']

    let errors: string[] = []
    errors = [...errors, ...validateFields(formData.contractSetupData)].filter(
      item => !notRequredSetupFields.includes(item),
    )

    setContractSetupFormFieldErrors(
      validateFields(formData.contractSetupData).filter(item => !notRequredSetupFields.includes(item)),
    )

    return errors
  }

  /**
   * A custom validation function the the Contract products form fields
   * @returns If the Contract products form fields have invalid values
   */
  const validateProductFormFields = (): string[] => {
    const notRequiredProductFields = [
      'deliveryDate',
      'installationCompensation',
      'updatedAt',
      'updatedBy',
      'createdBy',
      'contractTypePrices',
      'serialNumber',
    ]

    let errors: string[] = []
    errors = [
      ...errors,
      ...formData.productData.map(item => validateFields(item)).flat(),
      ...validateFields({extraCosts: formData.extraCosts}),
    ].filter(item => !notRequiredProductFields.some(notRequiredItem => item.includes(notRequiredItem)))

    setExtraCostsFormFieldErrors(validateFields({extraCosts: formData.extraCosts}))
    setProductFormFieldErrors(
      formData.productData.map(item =>
        validateFields(item).filter(
          item => !notRequiredProductFields.some(notRequiredItem => item.includes(notRequiredItem)),
        ),
      ),
    )

    return errors
  }

  /**
   * A custom validation function the the Contract customer form fields
   * @returns If the Contract customer form fields have invalid values
   */
  const validateCustomerFormFields = (): string[] => {
    const notRequiredCustomerFields = [
      'dateOfBirth',
      'phone',
      'street',
      'zipCode',
      'city',
      'creditScore',
      'houseNumber',
    ]

    if (formData.contractSetupData.type === ContractVariant.QUOTE) {
      notRequiredCustomerFields.push('email')
      notRequiredCustomerFields.push('personalNumber')
    }
    if (isB2B) notRequiredCustomerFields.push('lastName')

    let errors: string[] = []
    errors = [...errors, ...validateFields(formData.customerData)].filter(
      item => !notRequiredCustomerFields.includes(item),
    )
    setCustomerFormFieldErrors(
      validateFields(formData.customerData).filter(item => !notRequiredCustomerFields.includes(item)),
    )

    return errors
  }

  const onAcceptTransferAgreement = async () => {
    setIsAgreementModalVisible(false)
    if (
      formData.contractSetupData.type !== ContractVariant.QUOTE &&
      !formData.contractSetupData.creditCheckPassed &&
      !isB2B &&
      formData.contractSetupData.company?.country !== Country.FRANCE
    ) {
      await performCreditCheck()
    } else {
      if (!formData.contractSetupData.contractCode) await assignContractCode()
      setActiveStep(prev => prev + 1)
    }
  }

  /**
   * A function handling Next button click
   */
  const onRightBtnClick = () => {
    let errors: string[] = []
    switch (activeStep) {
      case 0:
        errors = validateSetupFormFields()
        break
      case 1:
        errors = validateProductFormFields()
        break
      case 2:
        errors = validateCustomerFormFields()
        break
    }

    if (errors.length === 0) {
      switch (activeStep) {
        case 2:
          createPdf()
          if (formData.contractSetupData.type === ContractVariant.CREATION) {
            setIsAgreementModalVisible(true)
          } else onAcceptTransferAgreement()
          break
        case 3:
          if (
            formData.contractSetupData.type === ContractVariant.CREATION &&
            (formData.contractSetupData.creditCheckPassed || isSuperAdmin())
          )
            setIsSignatureOptionsModalVisible(true)
          else onSaveContract()
          break
        default:
          setActiveStep(prev => prev + 1)
      }
    } else {
      setError({
        message: generateReadableErrorMessages(t, errors),
        name: '',
      })
    }
  }

  /**
   * Check if the user is at the first step
   * @returns If creation process is at first step
   */
  const isFirstStep = () => activeStep === 0

  /**
   * Back button should be visible if the user is not at the first step
   */
  const isBackBtnVisible = () => activeStep > (id ? 1 : 0)

  /**
   * Check if the user is at the last step
   * @returns If creation process is at last step
   */
  const isLastStep = () => activeStep === steps.length - 1

  /**
   * A function handling sending a Contract for signing
   * @param contractId Contract ID
   */
  const onSendForSigning = async (contractId: string) => {
    const {customerData} = formData
    const htmlGenerationPayload = {
      company: formData.contractSetupData.company as CompanyResponse,
      dealer: formData.contractSetupData.dealer as DealerResponse,
      length: formData.contractSetupData.length,
      contractType: formData.contractSetupData.contractType as ContractTypeResponse,
      quote: formData.contractSetupData.type === ContractVariant.QUOTE,
      customer: customerData,
      contractCode: formData.contractSetupData.contractCode || '',
      products: formData.productData,
      extraCosts: formData.extraCosts,
      signatory: formData.contractSetupData.officialSignatory || '',
      disclaimer: formData.contractSetupData.contractType?.disclaimer || '',
      createdAt: formData.contractSetupData.createdAt ?? formatDate(new Date(), userTimeFormat),
      expiry:
        formData.contractSetupData.expiry ??
        formatDate(
          addDays(
            parse(
              formData.contractSetupData.createdAt ?? formatDate(new Date(), userTimeFormat),
              userTimeFormat,
              new Date(),
            ),
            Number(formData?.contractSetupData?.contractType?.expiry || 7),
          ),
          userTimeFormat,
        ),
    }

    const generatedHtml = contractTypeTemplatesData?.[0]
      ? generateTemplateHtml(htmlGenerationPayload, contractTypeTemplatesData[0].htmlContent, imageUrl || '', currency)
      : generateHtml(t, htmlGenerationPayload, imageUrl || '', currency, userTimeFormat)

    const {email, firstName, lastName, phone} = customerData
    const file = await generatePdf(generatedHtml)
    const pdfWithNumbers = addTopRightPageNumbers(
      file,
      (i, total) => `${t(TranslationKey.Page)} ${i} ${t(TranslationKey.of)} ${total}`,
    )
    sendForSigningMutation({
      file: pdfWithNumbers.output('arraybuffer'),
      documentName: `${formData.contractSetupData.company?.name} - ${customerData.firstName} ${customerData.lastName} ${t(TranslationKey.Contract)} ${formatDate(new Date(), userTimeFormat)}`,
      contractId,
      greeting: signingFormData.greeting,
      recipientDto: {
        email: email || '',
        familyName: lastName || '-',
        givenName: firstName,
        language: signingFormData.language,
        notificationMethod: signingFormData.notificationMethod,
        order: 1,
        role: {
          action: 'sign',
          label: 'Signer',
          name: 'signer',
        },
        signingMethod: signingFormData.signingMethod,
        sms: signingFormData.sms,
        telephone: phone || '',
      },
    })
  }

  /**
   * A function handling clicking on the Send for signing button
   */
  const onSendForSigningBtnClick = () => {
    setIsSignatureOptionsModalVisible(false)
    onSaveContract()
  }

  /**
   * A function generating a Contract Status based on Contract Variant and Credit check value
   * @returns Contract status
   */
  const getContractStatus = () => {
    if (formData.contractSetupData.type === ContractVariant.QUOTE) return ContractStatus.QUOTE
    else if (!formData.contractSetupData.creditCheckPassed && !isSuperAdmin())
      return ContractStatus.AWAITING_VERIFICATION
    else return ContractStatus.ACTIVE
  }

  /**
   * A function handling the Save button click
   * If form is valid updates or creates a new Contract
   */
  const onSaveContract = (status?: ContractStatus) => {
    const contractPayloadData: ContractRequest = {
      contractCode: formData.contractSetupData.contractCode || '',
      companyId: formData.contractSetupData.company?.id || '',
      contractTypeId: formData.contractSetupData?.contractType?.id || '',
      creditCheckPassed: formData.contractSetupData.creditCheckPassed,
      customer: formData.customerData,
      dealerId: formData.contractSetupData?.dealer?.id || '',
      extraCosts: formData.extraCosts,
      expiry:
        formData.contractSetupData.expiry ??
        formatDate(
          new Date().setDate(new Date().getDate() + Number(formData?.contractSetupData?.contractType?.expiry || 7)),
          'yyyy-MM-dd',
        ),
      officialSignatory: formData.contractSetupData.officialSignatory || '',
      length: formData.contractSetupData.length,
      note: '',
      products: formData.productData.map(product => ({
        ...product,
        productId: product.id,
        accessories: product.accessories.map(accessory => ({...accessory, accessoryId: accessory.id})),
      })),
      status: status ?? getContractStatus(),
    }

    const clearAndExit = (id: string) => {
      setIsFormDirty(false)
      navigate(`${NavigationPathKey.CONTRACT_VIEW}/${id}`)
    }

    const handleSigning = (contractId: string) => {
      if (status !== ContractStatus.DRAFT && getContractStatus() === ContractStatus.ACTIVE) {
        onSendForSigning(contractId).then(() => {
          updateContractMutation(
            {body: {...contractPayloadData, status: ContractStatus.SENT_FOR_SIGNING}, id: contractId},
            {onSuccess: () => clearAndExit(contractId)},
          )
        })
      } else {
        clearAndExit(contractId)
      }
    }

    if (id) {
      updateContractMutation({body: contractPayloadData, id}, {onSuccess: () => handleSigning(id)})
    } else {
      createContractMutation(contractPayloadData, {
        onSuccess: ({id}) => handleSigning(id!),
      })
    }
  }

  const assignContractCode = async (): Promise<void> => {
    const {data: code} = await reserveCodeQuery()
    setContractSetupFormData(prev => ({
      ...prev,
      ...(formData.contractSetupData.company?.country === Country.FRANCE ? {creditCheckPassed: true} : {}),
      contractCode: code,
    }))
  }

  /**
   * A function handling Customer Credit check
   */
  const performCreditCheck = async (): Promise<void> => {
    if (!formData.contractSetupData.company?.country || !formData.customerData.personalNumber) return

    const purchasePrice = getTotalContractCost(formData.productData, formData.extraCosts)

    let contractCode = formData.contractSetupData.contractCode
    if (contractCode === '' || contractCode === undefined) {
      const {data} = await reserveCodeQuery()
      contractCode = data
    }

    creditCheckMutation(
      {
        country: formData.contractSetupData.company.country,
        personNumber: formData.customerData.personalNumber,
        purchasePrice,
        companyId: formData.contractSetupData.company.id,
        customer: {
          city: formData.customerData.city || '',
          country: formData.contractSetupData.company.country,
          firstName: formData.customerData.firstName,
          lastName: formData.customerData.lastName,
          street: formData.customerData.street || '',
          zipCode: formData.customerData.zipCode || '',
          houseNumber: formData.customerData.houseNumber || '',
        },
      },
      {
        onError: error => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-extra-semi
          ;(error as any).__handled = true
          setCreditCheckFailed(true)
        },
        onSettled: data => {
          setContractSetupFormData(prev => {
            return {
              ...prev,
              contractCode,
              creditCheckPassed: data?.passingCredit ?? false,
            }
          })
          setActiveStep(prev => prev + 1)
        },
      },
    )
  }

  // PDF TEMPLATE

  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [pdfTransferURl, setPdfTransferUrl] = useState<string>('')

  /**
   * Dynamically generated creation date
   */
  const creationDate = useMemo(
    () => formData.contractSetupData.createdAt ?? formatDate(new Date(), userTimeFormat),
    [formData, userTimeFormat],
  )

  /**
   * Dynamically generated expiry date
   */
  const expiryDate = useMemo(
    () =>
      formData.contractSetupData.expiry ??
      formatDate(
        addDays(
          parse(creationDate, userTimeFormat, new Date()),
          Number(formData?.contractSetupData?.contractType?.expiry || 7),
        ),
        userTimeFormat,
      ),
    [formData, userTimeFormat, creationDate],
  )

  const [loadingPdf, setLoadingPdf] = useState(false)

  const generateAndSetPdf = async (htmlData: string, setUrl: React.Dispatch<SetStateAction<string>>) => {
    setLoadingPdf(true)

    const doc = await generatePdf(htmlData)
    const pdfWithNumbers = addTopRightPageNumbers(
      doc,
      (i, total) => `${t(TranslationKey.Page)} ${i} ${t(TranslationKey.of)} ${total}`,
    )

    const pdfBlob = pdfWithNumbers.output('blob')
    const pdfObjectUrl = URL.createObjectURL(pdfBlob)
    setUrl(pdfObjectUrl)

    setLoadingPdf(false)
  }

  /**
   * Contract data needed for HTML generation
   */
  const dataForHtml = useMemo((): ContractHTMLData => {
    return {
      quote: formData.contractSetupData.type === ContractVariant.QUOTE,
      company: formData.contractSetupData.company as CompanyResponse,
      dealer: formData.contractSetupData.dealer as DealerResponse,
      contractType: formData.contractSetupData.contractType as ContractTypeResponse,
      length: formData.contractSetupData.length,
      customer: formData.customerData,
      products: formData.productData,
      extraCosts: formData.extraCosts,
      signatory: formData.contractSetupData.officialSignatory || '',
      disclaimer: formData.contractSetupData.contractType?.disclaimer || '',
      expiry: expiryDate,
      createdAt: creationDate,
      contractCode: formData.contractSetupData.contractCode || '',
    }
  }, [formData])

  const createPdf = async () => {
    const customerTemplate = contractTypeTemplatesData?.[0]?.htmlContent

    const htmlData = customerTemplate
      ? generateTemplateHtml(dataForHtml, customerTemplate, imageUrl || '', currency)
      : generateHtml(t, dataForHtml, imageUrl || '', currency, userTimeFormat)

    generateAndSetPdf(htmlData, setPdfUrl)

    const htmlTransferData = transferTemplate
      ? generateTemplateHtml(dataForHtml, transferTemplate, imageUrl || '', currency)
      : generateHtml(t, dataForHtml, imageUrl || '', currency, userTimeFormat)
    generateAndSetPdf(htmlTransferData, setPdfTransferUrl)
  }

  // END OF PDF TEMPLATE

  return {
    onRightBtnClick,
    onCenterBtnClick,
    onLeftBtnClick,
    isFirstStep,
    isBackBtnVisible,
    isLastStep,
    onSendForSigningBtnClick,
    onSaveContract,
    setIsSignatureOptionsModalVisible,
    pdfTransferURl,
    pdfUrl,
    isAgreementModalVisible,
    setIsAgreementModalVisible,
    onAcceptTransferAgreement,
    loadingPdf,
    creditCheckFailed,
    company,
    signingFormData,
    isSignatureOptionsModalVisible,
    t,
    formData,
    activeStep,
    steps,
  }
}
